type ScopeForSystemResourceType = {
  type: string;
  supportsCeilingScope: boolean;
};

type AttributeView = {
  displayName: string;
  systemResourceTypeId: string;
  attributeKeyJsonPath: string;
  sequence: number;
  isHyperlink: boolean;
};

export class SystemResourceType {
  static mandatoryAttributes = ["systemId", "name", "scope", "attributesView"];

  id: string;
  systemId: string;
  name: string;
  scope: ScopeForSystemResourceType;
  attributesView: Array<AttributeView>;

  constructor(input: Object) {
    for (const [key, value] of Object.entries(input)) {
      this[key] = JSON.parse(JSON.stringify(value));
    }
    const inputKeys = Array.from(Object.keys(input));
    const missedMandatoryFields = SystemResourceType.mandatoryAttributes.filter(
      (mandatoryAttribute) => !inputKeys.includes(mandatoryAttribute)
    );
    if (missedMandatoryFields.length > 0) {
      console.log(missedMandatoryFields.join(", "));
      throw new Error(
        `Following fields are not present on creating System Resource Type object ${missedMandatoryFields.join(
          ", "
        )}`
      );
    }
  }

  dynamicColumns(): Array<string> {
    return this.attributesView
      .sort((a: AttributeView, b: AttributeView) => a.sequence - b.sequence)
      .map((attributeSchema: AttributeView) => attributeSchema.displayName);
  }

  identifier(): { systemId: string; name: string } {
    return {
      systemId: this.systemId,
      name: this.name,
    };
  }

  optionAttributes(): { label: string; value: string } {
    return {
      label: this.name,
      value: this.name,
    };
  }

  isScopeApplicable(): boolean{
    return this.scope.type === "APPLICABLE";
  }
}

export class SystemResourceTypes {
  types: Array<SystemResourceType>;

  constructor(inputs: Array<Object>) {
    this.types = inputs.map((input) => new SystemResourceType(input));
  }

  isEmpty(): boolean {
    return this.types.length === 0;
  }

  ids(): Array<{ systemId: string; name: string }> {
    return this.types.map((type) => type.identifier());
  }

  getByName(name: string): SystemResourceType | null {
    return this.types.find((type) => type.name === name) ?? null;
  }

  getById(id: string): SystemResourceType | null {
    return this.types.find((type) => type.id === id) ?? null;
  }

  options(
    forUser: boolean,
    isAccessVisibilityForSisenseDatasourceFeatureEnabled: boolean
  ): Array<{ value: string; label: string }> {
    if (isAccessVisibilityForSisenseDatasourceFeatureEnabled && forUser) {
      return this.types.map((type) => type.optionAttributes());
    } else {
      const sisenseDashboard = this.getByName("Sisense Dashboard");
      return sisenseDashboard ? [sisenseDashboard.optionAttributes()] : [];
    }
  }
}
